import { Injectable } from '@angular/core';

import * as Highcharts from 'highcharts/highcharts';
import Highcharts3D from 'highcharts/highcharts-3d';
import HighchartsMore from 'highcharts/highcharts-more';

import Accessibility from 'highcharts/modules/accessibility';
import Annotations from 'highcharts/modules/annotations';
import ArrowSymbols from 'highcharts/modules/arrow-symbols';
import BoostCanvas from 'highcharts/modules/boost-canvas';
import Boost from 'highcharts/modules/boost';
import BrokenAxis from 'highcharts/modules/broken-axis';
import Bullet from 'highcharts/modules/bullet';
import CurrentDateIndicator from 'highcharts/modules/current-date-indicator';
import Cylinder from 'highcharts/modules/cylinder';
import Data from 'highcharts/modules/data';
import DataGrouping from 'highcharts/modules/datagrouping';
import DependencyWheel from 'highcharts/modules/dependency-wheel';
import DotPlot from 'highcharts/modules/dotplot';
import DragPanes from 'highcharts/modules/drag-panes';
import DraggablePoints from 'highcharts/modules/draggable-points';
import Drilldown from 'highcharts/modules/drilldown';
import ExportData from 'highcharts/modules/export-data';
import Exporting from 'highcharts/modules/exporting';
import FullScreen from 'highcharts/modules/full-screen';
import Funnel from 'highcharts/modules/funnel';
import Funnel3D from 'highcharts/modules/funnel3d';
import GridAxis from 'highcharts/modules/grid-axis';
import Heatmap from 'highcharts/modules/heatmap';
import HistogramBellCurve from 'highcharts/modules/histogram-bellcurve';
import ItemSeries from 'highcharts/modules/item-series';
import NetworkGraph from 'highcharts/modules/networkgraph';
import NoDataToDisplay from 'highcharts/modules/no-data-to-display';
import OfflineExporting from 'highcharts/modules/offline-exporting';
import OldiePolyfills from 'highcharts/modules/oldie-polyfills';
import Oldie from 'highcharts/modules/oldie';
import OverlappingDataLabels from 'highcharts/modules/overlapping-datalabels';
import ParallelCoordinates from 'highcharts/modules/parallel-coordinates';
import Pareto from 'highcharts/modules/pareto';
import Pathfinder from 'highcharts/modules/pathfinder';
import PatternFill from 'highcharts/modules/pattern-fill';
import PriceIndicator from 'highcharts/modules/price-indicator';
import Pyramid3D from 'highcharts/modules/pyramid3d';
import Sankey from 'highcharts/modules/sankey';
import SeriesLabel from 'highcharts/modules/series-label';
import SolidGauge from 'highcharts/modules/solid-gauge';
import StaticScale from 'highcharts/modules/static-scale';
import StreamGraph from 'highcharts/modules/streamgraph';
import Sunburst from 'highcharts/modules/sunburst';
import TileMap from 'highcharts/modules/tilemap';
import Timeline from 'highcharts/modules/timeline';
import Treegrid from 'highcharts/modules/treegrid';
import Treemap from 'highcharts/modules/treemap';
import VariablePie from 'highcharts/modules/variable-pie';
import Variwide from 'highcharts/modules/variwide';
import Vector from 'highcharts/modules/vector';
import Venn from 'highcharts/modules/venn';
import Windbarb from 'highcharts/modules/windbarb';
import Wordcloud from 'highcharts/modules/wordcloud';
import XRange from 'highcharts/modules/xrange';

import * as jspdf from "jspdf";
import * as svg2pdf from "svg2pdf.js";
import {ReportUtilService} from "../report/report-util.service";

@Injectable({
  providedIn: 'root'
})
export class ChartService {

  constructor(private reportUtilService : ReportUtilService) {}

  init() {
    Highcharts3D(Highcharts);
    HighchartsMore(Highcharts);
    Sankey(Highcharts); // Must be before DependencyWheel
    Accessibility(Highcharts);
    Annotations(Highcharts);
    ArrowSymbols(Highcharts);
    BoostCanvas(Highcharts);
    Boost(Highcharts);
    BrokenAxis(Highcharts);
    Bullet(Highcharts);
    CurrentDateIndicator(Highcharts);
    Cylinder(Highcharts);
    Data(Highcharts);
    DataGrouping(Highcharts);
    DependencyWheel(Highcharts); // Must be after Sankey
    DotPlot(Highcharts);
    DragPanes(Highcharts);
    DraggablePoints(Highcharts);
    Drilldown(Highcharts);
    ExportData(Highcharts);
    Exporting(Highcharts);
    FullScreen(Highcharts);
    Funnel(Highcharts);
    Funnel3D(Highcharts);
    GridAxis(Highcharts);
    Heatmap(Highcharts);
    HistogramBellCurve(Highcharts);
    ItemSeries(Highcharts);
    NetworkGraph(Highcharts);
    NoDataToDisplay(Highcharts);
    OfflineExporting(Highcharts);
    OldiePolyfills(Highcharts);
    Oldie(Highcharts);
    OverlappingDataLabels(Highcharts);
    ParallelCoordinates(Highcharts);
    Pareto(Highcharts);
    Pathfinder(Highcharts);
    PatternFill(Highcharts);
    PriceIndicator(Highcharts);
    Pyramid3D(Highcharts);
    SeriesLabel(Highcharts);
    SolidGauge(Highcharts);
    StaticScale(Highcharts);
    StreamGraph(Highcharts);
    Sunburst(Highcharts);
    TileMap(Highcharts);
    Timeline(Highcharts);
    Treegrid(Highcharts);
    Treemap(Highcharts);
    VariablePie(Highcharts);
    Variwide(Highcharts);
    Vector(Highcharts);
    Venn(Highcharts);
    Windbarb(Highcharts);
    Wordcloud(Highcharts);
    XRange(Highcharts);


    const self = this;
    const globalOptions = {
      // @ts-ignore - Highcharts d.ts is wrong
      colors: Highcharts.map(Highcharts.getOptions().colors, (color) => {
        return {
          radialGradient: {
            cx: 0.5,
            cy: 0.3,
            r: 0.7
          },
          stops: [
            [0, color],
            // @ts-ignore - Highcharts d.ts is wrong
            [1, Highcharts.Color(color).brighten(-0.3).get('rgb')] // darken
          ]
        };
      }),
      // @ts-ignore - Highcharts d.ts is wrong
      // NOTE: we modify the chart context menu to remove some items that are not needed or
      // have a poor user experience.
      exporting: {
        buttons: {
          contextButton: {
            menuItems: [
              {
              textKey: 'printChart',
              onclick: function () {
                this.print();
              }
            }, {
              separator: true
            }, {
              textKey: 'downloadPNG',
              onclick: function () {
                this.exportChart({
                  type: 'image/png'
                }, {});
              }
            }, {
              textKey: 'downloadJPEG',
              onclick: function () {
                this.exportChart({
                  type: 'image/jpeg'
                }, {});
              }
            }, {
              textKey: 'downloadPDF',
              onclick: function () {
                this.exportChart({
                  type: 'application/pdf'
                }, {});
              }
            }, {
              textKey: 'downloadSVG',
              onclick: function () {
                this.exportChart({
                  type: 'image/svg+xml'
                }, {});
              }
            }, {
                separator: true
              }, {
                textKey: 'downloadCSV',
                onclick: function () {
                  this.options = self.reportUtilService.setCsvOptions(this.options);
                  this.downloadCSV();
                }
              },
              {
                textKey: 'viewData',
                onclick: function () {
                  this.viewData();
                }
              },
              {
                textKey: 'hideData',
                onclick: function () {
                  if (this.dataTableDiv) {
                    this.dataTableDiv.remove();
                    this.dataTableDiv = undefined;
                  }
                }
              },
            ]
          }
        }
      }
    };

    Highcharts.setOptions(globalOptions as any);

    // Add labels for our custom menu items.
    Highcharts.defaultOptions.lang['hideData'] = 'Hide data table';

    if (!((window as any).jsPDF)) {
      (window as any).jsPDF = jspdf;
    }

    if (!((window as any).svg2pdf)) {
      (window as any).svg2pdf = svg2pdf;
    }

  }
}
